import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import {
  getAllClientsApi,
  followClientApi,
  unfollowClientApi,
  getFollowingClubsApi,
  followMultipleClientsApi,
  getLinkedMembersApi,
} from "../../api/ClientActions";

// ==========================================
// STATE INTERFACE
// ==========================================
interface ClientFollowState {
  clients: any[];
  followingClubs: any[];
  linkedMembers: any[];

  loading: boolean;
  followingLoading: boolean;
  multipleFollowLoading: boolean;
  linkedMembersLoading: boolean;
  unfollowLoading: boolean;
}

// ==========================================
// INITIAL STATE
// ==========================================
const initialState: ClientFollowState = {
  clients: [],
  followingClubs: [],
  linkedMembers: [],

  loading: false,
  followingLoading: false,
  multipleFollowLoading: false,
  linkedMembersLoading: false,
  unfollowLoading: false,
};

// ==========================================
// FETCH SUGGESTED CLUBS
// ==========================================
export const fetchAllClients =
  createAsyncThunk(
    "clientFollow/fetchAllClients",

    async (payload: any) => {
      const res =
        await getAllClientsApi(payload);

      return (
        res?.data ||
        res?.clients ||
        []
      );
    }
  );

// ==========================================
// FOLLOW SINGLE CLUB
// ==========================================
export const followClient =
  createAsyncThunk(
    "clientFollow/followClient",

    async (payload: any) => {
      const res =
        await followClientApi(payload);

      return {
        ...payload,

        status:
          res?.success === true ||
          res?.status === true,

        message:
          res?.message,

        data:
          res?.data,
      };
    }
  );

// ==========================================
// UNFOLLOW SINGLE CLUB
// ==========================================
export const unfollowClient =
  createAsyncThunk(
    "clientFollow/unfollowClient",

    async (payload: any) => {
      const res =
        await unfollowClientApi(
          payload
        );

      return {
        ...payload,

        status:
          res?.success === true ||
          res?.status === true,

        message:
          res?.message,

        data:
          res?.data,
      };
    }
  );

// ==========================================
// FOLLOW MULTIPLE CLUBS
// ==========================================
export const followMultipleClients =
  createAsyncThunk(
    "clientFollow/followMultipleClients",

    async (payload: any) => {
      const res =
        await followMultipleClientsApi(
          payload
        );

      return {
        ...payload,

        status:
          res?.success === true ||
          res?.status === true,

        message:
          res?.message,

        data:
          res?.data,
      };
    }
  );

// ==========================================
// FETCH FOLLOWING CLUBS
// ==========================================
export const fetchFollowingClubs =
  createAsyncThunk(
    "clientFollow/fetchFollowingClubs",

    async (payload: any) => {
      const res =
        await getFollowingClubsApi(
          payload
        );

      return (
        res?.data ||
        res?.clients ||
        []
      );
    }
  );

// ==========================================
// FETCH LINKED MEMBERS
// ==========================================
export const fetchLinkedMembers =
  createAsyncThunk(
    "clientFollow/fetchLinkedMembers",

    async (payload: any) => {
      const res =
        await getLinkedMembersApi(
          payload
        );

      return res?.data || [];
    }
  );

// ==========================================
// SLICE
// ==========================================
const clientFollowSlice =
  createSlice({
    name: "clientFollow",

    initialState,

    reducers: {},

    extraReducers:
      (builder) => {
        builder

          // ==========================================
          // FETCH SUGGESTED CLUBS
          // ==========================================
          .addCase(
            fetchAllClients.pending,
            (state) => {
              state.loading = true;
            }
          )

          .addCase(
            fetchAllClients.fulfilled,
            (
              state,
              action
            ) => {
              state.loading = false;

              state.clients =
                action.payload;
            }
          )

          .addCase(
            fetchAllClients.rejected,
            (state) => {
              state.loading = false;
            }
          )

          // ==========================================
          // FOLLOW SINGLE CLUB
          // ==========================================
          .addCase(
            followClient.fulfilled,
            (
              state,
              action
            ) => {
              if (
                action.payload
                  .status === true
              ) {
                // Remove followed club
                // from Suggested Clubs
                state.clients =
                  state.clients.filter(
                    (client) =>
                      Number(
                        client.client_id
                      ) !==
                      Number(
                        action.payload
                          .client_id
                      )
                  );
              }
            }
          )

          // ==========================================
          // UNFOLLOW SINGLE CLUB
          // ==========================================
          .addCase(
            unfollowClient.pending,
            (state) => {
              state.unfollowLoading =
                true;
            }
          )

          .addCase(
            unfollowClient.fulfilled,
            (
              state,
              action
            ) => {
              state.unfollowLoading =
                false;

              if (
                action.payload
                  .status === true
              ) {
                // Remove club from
                // "Clubs you follow"
                state.followingClubs =
                  state.followingClubs.filter(
                    (club) =>
                      Number(
                        club.client_id
                      ) !==
                      Number(
                        action.payload
                          .client_id
                      )
                  );
              }
            }
          )

          .addCase(
            unfollowClient.rejected,
            (state) => {
              state.unfollowLoading =
                false;
            }
          )

          // ==========================================
          // FOLLOW MULTIPLE CLUBS
          // ==========================================
          .addCase(
            followMultipleClients.pending,
            (state) => {
              state.multipleFollowLoading =
                true;
            }
          )

          .addCase(
            followMultipleClients.fulfilled,
            (
              state,
              action
            ) => {
              state.multipleFollowLoading =
                false;

              if (
                action.payload
                  .status === true
              ) {
                const followedIds =
                  (
                    action.payload
                      .client_ids || []
                  ).map(Number);

                state.clients =
                  state.clients.filter(
                    (client) =>
                      !followedIds.includes(
                        Number(
                          client.client_id
                        )
                      )
                  );
              }
            }
          )

          .addCase(
            followMultipleClients.rejected,
            (state) => {
              state.multipleFollowLoading =
                false;
            }
          )

          // ==========================================
          // FETCH FOLLOWING CLUBS
          // ==========================================
          .addCase(
            fetchFollowingClubs.pending,
            (state) => {
              state.followingLoading =
                true;
            }
          )

          .addCase(
            fetchFollowingClubs.fulfilled,
            (
              state,
              action
            ) => {
              state.followingLoading =
                false;

              state.followingClubs =
                action.payload;
            }
          )

          .addCase(
            fetchFollowingClubs.rejected,
            (state) => {
              state.followingLoading =
                false;
            }
          )

          // ==========================================
          // FETCH LINKED MEMBERS
          // ==========================================
          .addCase(
            fetchLinkedMembers.pending,
            (state) => {
              state.linkedMembersLoading =
                true;
            }
          )

          .addCase(
            fetchLinkedMembers.fulfilled,
            (
              state,
              action
            ) => {
              state.linkedMembersLoading =
                false;

              state.linkedMembers =
                action.payload;
            }
          )

          .addCase(
            fetchLinkedMembers.rejected,
            (state) => {
              state.linkedMembersLoading =
                false;
            }
          );
      },
  });

// ==========================================
// EXPORT REDUCER
// ==========================================
export default clientFollowSlice.reducer;